import { ChangeDetectionStrategy, Component, HostBinding, Input, OnChanges, OnInit, signal, SimpleChanges, inject, forwardRef, input } from '@angular/core';
import { NavigationDropdown, NavigationItem, NavigationLink } from '../../../interfaces/navigation-item.interface';
import { dropdownAnimation } from '../../../animations/dropdown.animation';
import { NavigationEnd, Router, RouterLinkActive, RouterLink } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs/operators';
import { NavigationService } from '../../../services/navigation.service';
import { TicketManagementSessionService } from '../../../../app/services/ticket-management-session.service';
import { trackByRoute } from '../../../utils/track-by';
import { MatRipple } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { AraSoonBadgeComponent } from '../../../../app/components/util/ara-soon-badge/ara-soon-badge.component';

@UntilDestroy()
@Component({
	selector: 'vex-sidenav-item',
	templateUrl: './sidenav-item.component.html',
	styleUrls: ['./sidenav-item.component.scss'],
	animations: [dropdownAnimation],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [MatRipple, RouterLinkActive, MatIcon, NgClass, RouterLink, MatTooltip, AraSoonBadgeComponent, forwardRef(() => SidenavItemComponent)],
})
export class SidenavItemComponent implements OnInit, OnChanges {
	private router = inject(Router);
	private ticketManagementSession = inject(TicketManagementSessionService);
	private navigationService = inject(NavigationService);

	@Input() item: NavigationItem;
	readonly level = input<number>(undefined);
	isOpen = signal(false);
	isHighlighted = signal(false);
	isActive = signal(false);
	isLoaded = signal(false);

	isLink = this.navigationService.isLink;
	isDropdown = this.navigationService.isDropdown;
	isSubheading = this.navigationService.isSubheading;

	protected readonly trackByRoute = trackByRoute;

	@HostBinding('class')
	get levelClass() {
		const classes = [`item-level-${this.level()}`];
		if (this.isDropdown(this.item) && this.level() === 0) {
			classes.push('has-dropdown-level-0');
		}
		return classes.join(' ');
	}

	ngOnInit() {
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				filter(() => this.isDropdown(this.item)),
				untilDestroyed(this)
			)
			.subscribe(() => this.onRouteChange());

		this.navigationService.openChange$
			.pipe(
				filter(() => this.isDropdown(this.item)),
				untilDestroyed(this)
			)
			.subscribe((item) => this.onOpenChange(item));
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes && changes.hasOwnProperty('item') && this.isDropdown(this.item)) {
			this.onRouteChange();
		}
	}

	toggleOpen() {
		this.isOpen.set(!this.isOpen());
		this.onRouteChange();
	}

	onOpenChange(item: NavigationDropdown) {
		if (this.isChildrenOf(this.item as NavigationDropdown, item)) {
			return;
		}

		if (this.hasActiveChilds(this.item as NavigationDropdown)) {
			return;
		}

		if (this.item !== item) {
			this.isOpen.set(false);
		}
	}

	onRouteChange() {
		if (!this.isLoaded()) {
			this.isActive.set(false);
			this.isHighlighted.set(false);
			this.isOpen.set(this.level() === 0);
			this.isLoaded.set(true);
		}
		if (this.hasActiveChilds(this.item as NavigationDropdown)) {
			this.isActive.set(true);
			this.isHighlighted.set(true);
			this.isOpen.set(true);
		} else {
			this.isActive.set(false);
			this.isHighlighted.set(false);
		}
	}

	isChildrenOf(parent: NavigationDropdown, item: NavigationDropdown) {
		if (parent.children.indexOf(item) !== -1) {
			return true;
		}

		return parent.children.filter((child) => this.isDropdown(child)).some((child) => this.isChildrenOf(child as NavigationDropdown, item));
	}

	hasActiveChilds(parent: NavigationDropdown) {
		return parent.children.some((child) => {
			if (this.isDropdown(child)) {
				return this.hasActiveChilds(child);
			}

			if (this.isLink(child) && !this.isFunction(child.route)) {
				return this.router.isActive(child.route as string, false);
			}
		});
	}

	isFunction(prop: NavigationLink['route']) {
		return prop instanceof Function;
	}

	clearTicketManagementSession(item: NavigationLink) {
		this.ticketManagementSession.clearSession();
		this.router.navigate([item.route]);
	}
}
